import useLogout from 'containers/authentication/useLogout'
import { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import slugify from 'utils/slugify'
import Modal from 'components/Modal'

type MenuOption = {
  name: string
  isLink: boolean
  submenu?: MenuOption[]
  link?: string
}

const MENU_OPTIONS: MenuOption[] = [
  {
    name: 'Documentation',
    isLink: true,
    link: 'https://docs.mayainsights.com/',
  },
  {
    name: 'Legal',
    isLink: false,
    submenu: [
      {
        name: 'Privacy Policy',
        isLink: true,
        link: 'https://www.mayainsights.com/privacy-policy/',
      },
      {
        name: 'Terms of Service',
        isLink: true,
        link: 'https://www.mayainsights.com/terms-and-conditions/',
      },
    ],
  },
]

const AccountMenu = ({ currentOrganization, setIsAccountMenuOpen }) => {
  const logOut = useLogout()
  const history = useHistory()
  const [isMobile, setIsMobile] = useState(false)
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const Submenu = ({ option, submenu }) => {
    const [hoveringMenu, setHoveringMenu] = useState(false)

    return (
      <li
        className="sub-menu"
        onClick={(e) => {
          e.stopPropagation()
          if (isMobile) {
            setActiveSubmenu(option.name)
          }
        }}
        onMouseOver={() => !isMobile && setHoveringMenu(true)}
        onMouseLeave={() => !isMobile && setHoveringMenu(false)}
      >
        {option.name}
        <span className="material-icons-outlined">keyboard_arrow_right</span>
        {hoveringMenu && !isMobile && <div>{submenu}</div>}
      </li>
    )
  }

  const generateMenu = (options: MenuOption[]) => {
    return options.map((option) => {
      if (option.isLink) {
        return (
          <li>
            <a target="_blank" rel="noreferrer" href={option.link!}>
              {option.name}
            </a>
          </li>
        )
      } else {
        const submenu = generateMenu(option.submenu!)

        return <Submenu option={option} submenu={submenu}></Submenu>
      }
    })
  }

  const menuContent = (
    <ul className="menu-options">
      <li
        onClick={() => {
          history.push(
            `/organization/${slugify(
              currentOrganization.name
            )}/account-settings/profile`
          )
          setIsAccountMenuOpen(false)
        }}
        className="custom"
      >
        <p>Settings</p>
        <span className="material-icons-outlined">manage_accounts</span>
      </li>
      {generateMenu(MENU_OPTIONS)}
      <li onClick={() => logOut(false)} className="log-out-button custom">
        <p>Logout</p>
        <span className="material-icons-outlined">logout</span>
      </li>
    </ul>
  )

  if (isMobile) {
    // Find the active submenu option
    const activeOption = MENU_OPTIONS.find(opt => opt.name === activeSubmenu)

    return (
      <Modal
        show={true}
        onHide={() => {
          setActiveSubmenu(null)
          setIsAccountMenuOpen(false)
        }}
        className="account-menu-modal"
      >
        <div className="account-menu-modal-content">
          {/* Main menu view */}
          <div className={`menu-main ${activeSubmenu ? 'slide-left' : ''}`}>
            <div className="modal-header">
              <h2>Account Menu</h2>
              <button className="close-button" onClick={() => setIsAccountMenuOpen(false)}>
                <span className="material-icons">close</span>
              </button>
            </div>
            {menuContent}
          </div>

          {/* Submenu view */}
          {activeSubmenu && (
            <div className="menu-submenu slide-in">
              <div className="modal-header">
                <button
                  className="back-button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setActiveSubmenu(null)
                  }}
                >
                  <span className="material-icons">arrow_back</span>
                </button>
                <h2>{activeSubmenu}</h2>
                <button className="close-button" onClick={() => setIsAccountMenuOpen(false)}>
                  <span className="material-icons">close</span>
                </button>
              </div>
              <ul className="menu-options">
                {activeOption?.submenu?.map((subOption) => (
                  <li key={subOption.name}>
                    <a target="_blank" rel="noreferrer" href={subOption.link!}>
                      {subOption.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Modal>
    )
  }

  return menuContent
}

export default AccountMenu
